import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useOptionsStore } from '../../Store/optionsStore';
import useWebSocketDataStore from '../../Store/WebSocketDataStore';
import { useChainSettingsStore } from '../../Store/chainSettingsStore';
import { buildChain, atmBoundaryStrike } from '../../Processors/chainNormalizer';
import { nearestStrike } from '../../Utils/gammaLevels';
import { esAdjustedStrike, latestIndexSpot } from '../../Utils/esAdjustedPrice';
import { fmt0 } from '../../Utils/format';
import { netVolClass, deltaClass, gammaClass, tradeCountClass, volumeClass, OPTION_CHAIN_HEAT_CSS_VARIABLES } from '../../Utils/scales';
import '../../Styles/optionChain.css';
import '../../Styles/condensedChain.css';
import { useDataState } from '../../Hooks/useDataState';
import { WidgetPanelDataStatus } from '../Dashboard/WidgetPanelSlot';

// Behavioural contract for the height-derived ATM window (TD077 / issue #13):
//
// TARGET_ROW_HEIGHT_PX = 22. Condensed cells are 11px type plus a 1px row
// border; 22px leaves a readable band of padding. With gridAutoRows: 1fr,
// N = floor(height / 22) keeps actual row height in [22px, ~24px] except at
// the clamps.
//
// MIN_VISIBLE_STRIKES = 9. Same width as the old ATM block (centre +/- 4). A
// shorter panel still shows a usable ladder rather than collapsing to a
// handful of crushed-or-empty rows.
//
// MAX_VISIBLE_STRIKES = 31. About 2x today's 15-row cap. Tall enough that a
// large panel shows more of the chain, short of dumping the whole ladder into
// a widget whose point is being condensed. Past this, leftover height
// stretches rows instead of adding strikes.
//
// N hysteresis: 6px past the floor-bucket boundary (~1/4 of a row). Resize
// observer updates are not debounced; the band is what stops N flickering
// while a panel edge is dragged across a bucket line.
export const TARGET_ROW_HEIGHT_PX = 22;
export const MIN_VISIBLE_STRIKES = 9;
export const MAX_VISIBLE_STRIKES = 31;
export const N_HYSTERESIS_PX = 6;

const CALL_COLS = [
  { key: 'accumulatedVolume', label: 'Vol', style: 'vol' },
  { key: 'totalNetVol', label: 'NetVol', classFor: netVolClass },
  { key: 'cumulativeDeltaNet', label: 'D Net', classFor: deltaClass },
  { key: 'cumulativeGammaNet', label: 'G Net', classFor: gammaClass },
  { key: 'tradeCount', label: 'Trades', classFor: tradeCountClass },
];
const PUT_COLS = [
  { key: 'tradeCount', label: 'Trades', classFor: tradeCountClass },
  { key: 'totalNetVol', label: 'NetVol', classFor: netVolClass, classOpts: { invert: true }, note: 'Put NetVol color follows market direction: sold puts are green; bought puts are red.' },
  { key: 'cumulativeDeltaNet', label: 'D Net', classFor: deltaClass },
  { key: 'cumulativeGammaNet', label: 'G Net', classFor: gammaClass },
  { key: 'accumulatedVolume', label: 'Vol', style: 'vol' },
];

const f1 = (v) =>
  v == null || v === '' ? '--'
    : Number(v).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

function clampVisibleCount(n) {
  return Math.max(MIN_VISIBLE_STRIKES, Math.min(MAX_VISIBLE_STRIKES, n));
}

export function visibleCountFromHeight(heightPx, currentN = null) {
  if (!Number.isFinite(heightPx) || heightPx <= 0) {
    return currentN ?? MIN_VISIBLE_STRIKES;
  }
  const naive = clampVisibleCount(Math.floor(heightPx / TARGET_ROW_HEIGHT_PX));
  if (currentN == null) return naive;
  if (naive === currentN) return currentN;
  if (naive > currentN) {
    const growAt = (currentN + 1) * TARGET_ROW_HEIGHT_PX + N_HYSTERESIS_PX;
    return heightPx >= growAt ? naive : currentN;
  }
  const shrinkAt = currentN * TARGET_ROW_HEIGHT_PX - N_HYSTERESIS_PX;
  return heightPx <= shrinkAt ? naive : currentN;
}

export function buildRows(chain, spot, boundary, visibleN = MIN_VISIBLE_STRIKES) {
  const strikes = chain?.strikes;
  if (!Array.isArray(strikes) || strikes.length === 0) return [];

  const center = boundary ?? (spot != null ? nearestStrike(strikes, spot) : null);
  if (center == null) return [];
  const ci = strikes.indexOf(center);
  if (ci < 0) return [];

  const n = Math.min(Math.max(1, visibleN), strikes.length);
  let lo = ci - Math.floor((n - 1) / 2);
  let hi = lo + n - 1;
  if (lo < 0) {
    hi = Math.min(strikes.length - 1, hi - lo);
    lo = 0;
  }
  if (hi > strikes.length - 1) {
    lo = Math.max(0, lo - (hi - (strikes.length - 1)));
    hi = strikes.length - 1;
  }

  const out = [];
  for (let i = lo; i <= hi; i++) {
    const k = strikes[i];
    out.push({ type: 'strike', k, atm: k === center });
  }
  return out;
}

function Cell({ value, align, styleRole, first, noRight, extraClass = '' }) {
  const classes = ['oc-cell'];
  if (extraClass) classes.push(extraClass);
  classes.push(align === 'left' ? 'oc-cell--left' : align === 'right' ? 'oc-cell--right' : 'oc-cell--center');
  if (styleRole === 'vol') classes.push('oc-cell--vol');
  if (first) classes.push('oc-cell--first');
  if (noRight) classes.push('oc-cell--no-r');
  const text = fmt0(value);
  return <div className={classes.join(' ')} title={String(text)}>{text}</div>;
}

export default function CondensedChainTable({ className = '', view = 'spx' }) {
  const rowsBySym = useOptionsStore(s => s.rowsBySym);
  const chainSnaps = useOptionsStore(s => s.chainSnaps);
  const liveSpot = useOptionsStore(s => s.spot);
  const indexData = useWebSocketDataStore(s => s.indexData);
  const aggregationState = useWebSocketDataStore(s => s.aggregationState);
  const chain = useMemo(() => buildChain(rowsBySym), [rowsBySym]);
  const lastSnapT = chainSnaps.length > 0 ? chainSnaps[chainSnaps.length - 1].t : null;
  const dataState = useDataState({ hasData: chain.strikes.length > 0, lastUpdated: lastSnapT });
  const spxSpot = chain?.S ?? liveSpot ?? null;
  const boundary = useMemo(() => atmBoundaryStrike(chain), [chain]);
  const rowsRef = useRef(null);
  const visibleNRef = useRef(MIN_VISIBLE_STRIKES);
  const [visibleN, setVisibleN] = useState(MIN_VISIBLE_STRIKES);
  const rows = useMemo(
    () => buildRows(chain, spxSpot, boundary, visibleN),
    [chain, spxSpot, boundary, visibleN]
  );

  const isEsAdjusted = view === 'esAdjusted';
  const esSpot = useMemo(
    () => latestIndexSpot(indexData, aggregationState, 'ES'),
    [indexData, aggregationState]
  );
  const displayStrike = (strike) => (
    isEsAdjusted ? esAdjustedStrike(strike, spxSpot, esSpot) : strike
  );

  const inverted = useChainSettingsStore(s => s.condensedChainInverted);
  const toggleInverted = useChainSettingsStore(s => s.toggleCondensedChainInverted);
  const displayRows = useMemo(
    () => (inverted ? [...rows].reverse() : rows),
    [rows, inverted]
  );

  const gridCols = useMemo(() => {
    const fr = 'minmax(0,1fr)';
    const calls = Array(CALL_COLS.length).fill(fr).join(' ');
    const puts = Array(PUT_COLS.length).fill(fr).join(' ');
    return `${calls} clamp(4.5rem, 7vw, 6.5rem) ${puts}`;
  }, []);
  const totalCols = CALL_COLS.length + 1 + PUT_COLS.length;

  const hasRows = rows.length > 0;
  const showTable = hasRows && !(isEsAdjusted && esSpot == null);
  useLayoutEffect(() => {
    if (!showTable) return undefined;
    const el = rowsRef.current;
    if (!el) return undefined;
    const apply = (h) => {
      const next = visibleCountFromHeight(h, visibleNRef.current);
      if (next !== visibleNRef.current) {
        visibleNRef.current = next;
        setVisibleN(next);
      }
    };
    apply(el.clientHeight);
    const ro = new ResizeObserver(([entry]) => {
      apply(entry?.contentRect?.height || el.clientHeight || 0);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [showTable]);

  if (!rows.length) {
    return (
      <div className={`w-full oc-table cc-table ${className}`} style={{ height: '100%' }}>
        <div className="cc-empty">Loading...</div>
      </div>
    );
  }

  if (isEsAdjusted && esSpot == null) {
    return (
      <div className={`w-full oc-table cc-table ${className}`} style={{ height: '100%' }}>
        <div className="cc-empty">ES price unavailable</div>
      </div>
    );
  }

  return (
    <>
      <WidgetPanelDataStatus status={dataState.status} asOf={dataState.asOf} />
      <div className={`w-full oc-table cc-table ${className}`} style={{ ...OPTION_CHAIN_HEAT_CSS_VARIABLES, '--cols': totalCols, height: '100%', minHeight: 0 }}>
      <div className="border oc-border rounded-lg m-0 shadow-sm"
        style={{ minWidth: 0, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        <div className="sticky top-0 z-10 oc-header tracking-wide border-b oc-border"
          style={{ display: 'grid', gridTemplateColumns: gridCols, alignContent: 'start', minWidth: 0, flex: '0 0 auto' }}>
          {CALL_COLS.map((c, idx) => {
            const cls = ['oc-hcell', 'oc-hcell--right', 'text-xs',
              idx === 0 ? 'oc-hcell--first' : '', idx === CALL_COLS.length - 1 ? 'oc-hcell--no-r' : ''].join(' ');
            return <div key={`hC-${c.key}`} className={cls}>{c.label}</div>;
          })}
          <div className="oc-hcell oc-hcell--center text-xs oc-hcell--no-r" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <button
              type="button"
              className={`oc-strike-toggle ${inverted ? 'is-active' : ''}`}
              aria-label={inverted ? 'Strike order inverted; click to restore' : 'Click to invert strike order'}
              aria-pressed={inverted}
              onClick={toggleInverted}
              title={isEsAdjusted ? 'ES-adjusted labels from SPX option-chain strikes' : 'SPX strike labels'}
            >
              {isEsAdjusted ? 'ES ADJ' : 'STRIKE'}
              <span className="oc-strike-caret" aria-hidden="true">{inverted ? 'v' : '^'}</span>
            </button>
          </div>
          {PUT_COLS.map((c) => <div key={`hP-${c.key}`} className="oc-hcell oc-hcell--left text-xs" title={c.note}>{c.label}</div>)}
        </div>

        <div ref={rowsRef} className="oc-rows" style={{ flex: '1 1 auto', minHeight: 0, display: 'grid', gridAutoRows: '1fr', overflow: 'hidden' }}>
          {displayRows.map((row, i) => {
            const bucket = chain.map.get(row.k) || { call: null, put: null };
            const call = bucket.call;
            const put = bucket.put;
            const rowCls = ['border-b', 'oc-border', i % 2 ? 'oc-row' : 'oc-row--alt',
              row.atm ? 'cc-row--atm' : '',
              boundary != null && row.k === boundary
                ? (inverted ? 'oc-row--atmline-bottom' : 'oc-row--atmline-top')
                : ''].join(' ');
            const shownStrike = displayStrike(row.k);
            const title = isEsAdjusted
              ? `SPX strike ${f1(row.k)} / ES-adjusted ${f1(shownStrike)}`
              : `S=${f1(chain.S)}`;
            return (
              <div key={row.k} className={rowCls}
                style={{ display: 'grid', gridTemplateColumns: gridCols, minWidth: 0, minHeight: 0 }}>
                {CALL_COLS.map((col, idx) => {
                  const raw = call?.[col.key];
                  const extra = col.style === 'vol' ? volumeClass(raw, { step: 10000, maxBin: 10 }) : (col.classFor ? col.classFor(raw, col.classOpts) : '');
                  return <Cell key={`c-${row.k}-${col.key}`} value={raw} align="right" styleRole={col.style}
                    first={idx === 0} noRight={idx === CALL_COLS.length - 1} extraClass={extra} />;
                })}
                <div className="oc-cell oc-cell--center oc-strike" title={title}>{f1(shownStrike)}</div>
                {PUT_COLS.map((col, idx) => {
                  const raw = put?.[col.key];
                  const extra = col.style === 'vol' ? volumeClass(raw, { step: 10000, maxBin: 10 }) : (col.classFor ? col.classFor(raw, col.classOpts) : '');
                  return <Cell key={`p-${row.k}-${col.key}`} value={raw} align="left" styleRole={col.style}
                    first={idx === 0} noRight={idx === PUT_COLS.length - 1} extraClass={extra} />;
                })}
              </div>
            );
          })}
        </div>
      </div>
      </div>
    </>
  );
}
